import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  Cloud,
  Refresh,
} from '@mui/icons-material';
import { authAPI } from '../services/api';
import { Properties } from '../api/BackofficeApi';

const PropertiesPage: React.FC = () => {
  const [properties, setProperties] = useState<Properties | null>(null);
  const [loading, setLoading] = useState(true);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [propertiesDialogOpen, setPropertiesDialogOpen] = useState(false);
  const [editingProperties, setEditingProperties] = useState<Properties>({ minVersion: '', maxVersion: '' });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      setError('');
      const propertiesData = await authAPI.getProperties();
      setProperties(propertiesData);
    } catch (err: any) {
      console.error('Properties fetch error:', err);
      setError('Failed to load AWS properties');
    } finally {
      setLoading(false);
    }
  };

  const handleEditProperties = () => {
    if (properties) {
      setEditingProperties({ ...properties });
      setPropertiesDialogOpen(true);
    }
  };

  const handleSaveProperties = async () => {
    try {
      setPropertiesLoading(true);
      const updatedProperties = await authAPI.updateProperties(editingProperties);
      setProperties(updatedProperties);
      setPropertiesDialogOpen(false);
      setSnackbarMessage('AWS properties updated successfully');
      setSnackbarOpen(true);
    } catch (err: any) {
      console.error('Properties update error:', err);
      setSnackbarMessage('Failed to update AWS properties');
      setSnackbarOpen(true);
    } finally {
      setPropertiesLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setPropertiesDialogOpen(false);
    setEditingProperties({ minVersion: '', maxVersion: '' });
  };

  const handleRefresh = () => {
    fetchProperties();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button
          variant="contained"
          startIcon={<Refresh />}
          onClick={handleRefresh}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Cloud sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4">AWS Properties</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Manage application version constraints
            </Typography>
          </Box>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={handleEditProperties}
            disabled={!properties}
          >
            Edit Properties
          </Button>
        </Box>
      </Box>

      {/* Properties Cards */}
      {properties ? (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Cloud sx={{ mr: 1, color: 'warning.main' }} />
                  <Typography variant="h6" color="text.secondary">
                    Minimum Version
                  </Typography>
                </Box>
                <Typography variant="h4" color="primary">
                  {properties.minVersion}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  The minimum required version for client applications
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Cloud sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="h6" color="text.secondary">
                    Maximum Version
                  </Typography>
                </Box>
                <Typography variant="h4" color="primary">
                  {properties.maxVersion}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  The latest available version for client applications
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Alert severity="warning">
          No properties data available
        </Alert>
      )}

      {/* Properties Edit Dialog */}
      <Dialog open={propertiesDialogOpen} onClose={handleCancelEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit AWS Properties</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Minimum Version"
              value={editingProperties.minVersion}
              onChange={(e) => setEditingProperties({ ...editingProperties, minVersion: e.target.value })}
              margin="normal"
              helperText="Format: major.minor.patch.assembly (e.g., 1.0.0.0)"
              placeholder="1.0.0.0"
            />
            <TextField
              fullWidth
              label="Maximum Version"
              value={editingProperties.maxVersion}
              onChange={(e) => setEditingProperties({ ...editingProperties, maxVersion: e.target.value })}
              margin="normal"
              helperText="Format: major.minor.patch.assembly (e.g., 2.0.0.0)"
              placeholder="2.0.0.0"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveProperties} 
            variant="contained" 
            startIcon={<Save />}
            disabled={propertiesLoading}
          >
            {propertiesLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default PropertiesPage;

